import { useState } from 'react';
import { pushDataLayer } from '@/lib/gtm';
import { useLanguage } from '@/context/LanguageContext';
import { submitProductInquiry } from '@/lib/supabase';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Loader2, CheckCircle, Minus, Plus } from 'lucide-react';

const translations = {
  title: { de: 'Verfügbarkeit anfragen', en: 'Request Availability', it: 'Richiedi Disponibilità' },
  quantity: { de: 'Anzahl', en: 'Quantity', it: 'Quantità' },
  name: { de: 'Name', en: 'Name', it: 'Nome' },
  email: { de: 'E-Mail', en: 'Email', it: 'Email' },
  phone: { de: 'Telefon (optional)', en: 'Phone (optional)', it: 'Telefono (opzionale)' },
  message: { de: 'Nachricht (optional)', en: 'Message (optional)', it: 'Messaggio (opzionale)' },
  messagePlaceholder: { 
    de: 'Details wie Menge, Farbe oder Ihre Frage hinzufügen…', 
    en: 'Add details like quantity, color, or your question…', 
    it: 'Aggiungi dettagli come quantità, colore o la tua domanda…' 
  },
  send: { de: 'Anfrage senden', en: 'Send Request', it: 'Invia Richiesta' },
  sending: { de: 'Senden...', en: 'Sending...', it: 'Invio...' },
  successTitle: { de: 'Anfrage gesendet!', en: 'Request Sent!', it: 'Richiesta Inviata!' },
  successMessage: { 
    de: 'Ihre Anfrage für wurde gesendet. Wir werden Sie in Kürze kontaktieren.', 
    en: 'Your request for has been sent. We will contact you shortly.', 
    it: 'La tua richiesta per è stata inviata. Ti contatteremo a breve.' 
  },
  close: { de: 'Schließen', en: 'Close', it: 'Chiudi' },
  continueBrowsing: { de: 'Weiter stöbern', en: 'Continue Browsing', it: 'Continua a Sfogliare' },
  sku: { de: 'Art.-Nr.', en: 'SKU', it: 'Cod.' }
};

export function ProductInquiryModal({ product, isOpen, onClose }) {
  const { language, getLocalizedField } = useLanguage();
  const [quantity, setQuantity] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const t = (key) => translations[key]?.[language] || translations[key]?.en || key;
  const productName = getLocalizedField(product, 'name');
  
  // Max quantity from database, default to 99 if not set
  const maxQuantity = product?.quantity || 99;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const inquiryData = {
      type: 'single_product_inquiry',
      customer: {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        message: formData.message || null
      },
      products: [{
        name: productName,
        name_de: product.name_de,
        name_en: product.name_en,
        name_it: product.name_it,
        price: product.price,
        currency: product.currency || 'EUR',
        sku: product.sku,
        quantity: quantity,
        url: window.location.href,
        image_url: product.image_url
      }],
      language,
      submitted_at: new Date().toISOString()
    };

    const result = await submitProductInquiry(inquiryData);
    
    if (result.success) {
      pushDataLayer({
        event: 'product_inquiry',
        product_id: product.id,
        product_name: productName,
        product_sku: product.sku || undefined,
        product_price: product.price || undefined,
        product_currency: product.currency || 'EUR',
        inquiry_quantity: quantity,
      });
      setIsSuccess(true);
    }
    
    setIsSubmitting(false);
  };

  const handleClose = () => {
    // Reset state when closing
    setIsSuccess(false);
    setQuantity(1);
    setFormData({ name: '', email: '', phone: '', message: '' });
    onClose();
  };

  if (!product) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg bg-[#F9F8F6] border-[#E5E5E5] max-h-[90vh] overflow-y-auto">
        {isSuccess ? (
          <div className="text-center py-8" data-testid="inquiry-success">
            <CheckCircle className="h-16 w-16 text-[#4A5D4F] mx-auto mb-4" strokeWidth={1.5} />
            <DialogTitle className="font-serif text-2xl text-[#2C2C2C] mb-2">
              {t('successTitle')}
            </DialogTitle>
            <p className="font-sans text-[#5D5D5D] mb-6">
              {t('successMessage').replace('für', `für "${productName}"`).replace('for has', `for "${productName}" has`).replace('per è', `per "${productName}" è`)}
            </p>
            <Button
              onClick={handleClose}
              className="rounded-full bg-[#2C2C2C] text-white px-8 py-3 h-auto hover:bg-[#4A5D4F] transition-colors text-sm uppercase tracking-widest"
              data-testid="inquiry-close-btn"
            >
              {t('continueBrowsing')}
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-xl text-[#2C2C2C]">
                {t('title')}
              </DialogTitle>
            </DialogHeader>

            {/* Product Summary */}
            <div className="flex gap-4 py-4" data-testid="inquiry-product-summary">
              <div className="w-20 h-20 flex-shrink-0 rounded-sm overflow-hidden bg-[#E5E5E5]">
                {product.image_url && (
                  <img 
                    src={product.image_url} 
                    alt={productName}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-serif text-lg text-[#2C2C2C] truncate">
                  {productName}
                </h3>
                {product.price && product.price < 2000 && (
                  <p className="font-sans text-base text-[#2C2C2C]">
                    {product.price.toLocaleString('de-DE', { style: 'currency', currency: product.currency || 'EUR' })}
                  </p>
                )}
                {product.sku && (
                  <p className="font-sans text-xs text-[#5D5D5D]">
                    {t('sku')}: {product.sku}
                  </p>
                )}
              </div>
            </div>

            {/* Quantity Selector */}
            <div className="flex items-center justify-between py-3 border-y border-[#E5E5E5]">
              <Label className="font-sans text-sm text-[#5D5D5D]">
                {t('quantity')} {maxQuantity < 99 && <span className="text-xs text-[#9CA3AF]">(max {maxQuantity})</span>}
              </Label>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 rounded-full border-[#E5E5E5]"
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  disabled={quantity <= 1}
                  data-testid="quantity-minus"
                >
                  <Minus className="h-4 w-4" strokeWidth={1.5} />
                </Button>
                <span className="font-sans text-lg w-8 text-center" data-testid="quantity-value">
                  {quantity}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 rounded-full border-[#E5E5E5]"
                  onClick={() => setQuantity(q => Math.min(maxQuantity, q + 1))}
                  disabled={quantity >= maxQuantity}
                  data-testid="quantity-plus"
                >
                  <Plus className="h-4 w-4" strokeWidth={1.5} />
                </Button>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4 pt-4" data-testid="inquiry-form">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="inquiry-name" className="font-sans text-xs text-[#5D5D5D]">
                    {t('name')} *
                  </Label>
                  <Input
                    id="inquiry-name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="bg-white border-[#E5E5E5] focus:border-[#2C2C2C] h-10"
                    data-testid="inquiry-input-name"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="inquiry-email" className="font-sans text-xs text-[#5D5D5D]">
                    {t('email')} *
                  </Label>
                  <Input
                    id="inquiry-email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="bg-white border-[#E5E5E5] focus:border-[#2C2C2C] h-10"
                    data-testid="inquiry-input-email"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="inquiry-phone" className="font-sans text-xs text-[#5D5D5D]">
                  {t('phone')}
                </Label>
                <Input
                  id="inquiry-phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="bg-white border-[#E5E5E5] focus:border-[#2C2C2C] h-10"
                  data-testid="inquiry-input-phone"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="inquiry-message" className="font-sans text-xs text-[#5D5D5D]">
                  {t('message')}
                </Label>
                <Textarea
                  id="inquiry-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={2}
                  placeholder={t('messagePlaceholder')}
                  className="bg-white border-[#E5E5E5] focus:border-[#2C2C2C] resize-none"
                  data-testid="inquiry-input-message"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-full bg-[#2C2C2C] text-white px-8 py-3 h-auto hover:bg-[#4A5D4F] transition-colors text-sm uppercase tracking-widest"
                data-testid="inquiry-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('sending')}
                  </>
                ) : (
                  t('send')
                )}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
